/**
 * SigTDD Plan Data Model
 *
 * Represents traffic signal coordination data for Time-Distance Diagrams.
 *
 * Structure:
 *   Corridor (saved to DB)
 *   ├── intersections[] - shared base data (names, distances, speeds)
 *   └── plans[] - up to 10 timing variations
 *       └── timing[] - offsets and phases per intersection
 */

import { DEFAULTS } from '../lib/defaults';

// ============================================================================
// Phase Types
// ============================================================================

/** Phase direction — controls which projection lines include this phase */
export type PhaseDirection = 'NB' | 'SB' | 'NBSB' | 'NA';

/** Single phase timing within a signal cycle */
export interface Phase {
  id: string;            // 'A' | 'B' | 'C' | 'D' | 'E' | 'F'
  duration: number;      // seconds
  direction: PhaseDirection;  // which projection lines include this phase
}

/** Check if a phase is active for a given drawing direction */
export function phaseMatchesDirection(
  phase: Phase,
  drawDirection: 'north' | 'south'
): boolean {
  if (phase.direction === 'NBSB') return true;
  if (phase.direction === 'NA') return false;
  return (phase.direction === 'NB' && drawDirection === 'north')
      || (phase.direction === 'SB' && drawDirection === 'south');
}

// ============================================================================
// Intersection Types
// ============================================================================

/** Base intersection data shared across all timing plans */
export interface IntersectionBase {
  id: string;             // UUID for React keys
  sequenceNo: number;     // 1, 2, 3... (No. field from CSV)
  name: string;           // Legacy full name — kept for backward compat
  crossStreet: string;    // Cross street only (e.g., "Oak St")
  distanceToNext: number; // metres to next intersection (Dist N)
  speedNorth: number;     // km/h (V N)
  speedSouth: number;     // km/h (V S)
}

/** Timing data for a single intersection within a timing plan */
export interface IntersectionTiming {
  intersectionId: string; // References IntersectionBase.id
  offset: number;         // seconds (OS)
  phases: Phase[];        // up to 6 phases (A-F)
}

/**
 * Full intersection data (base + timing) for rendering
 * This is the "merged" view used by canvas and data grid
 */
export interface Intersection extends IntersectionBase {
  offset: number;         // seconds (OS) - from active timing plan
  phases: Phase[];        // from active timing plan
}

// ============================================================================
// Timing Plan Types
// ============================================================================

/** A timing plan within a corridor (up to 10 per corridor) */
export interface TimingPlan {
  id: string;             // UUID
  name: string;           // e.g., "AM Peak NB", "PM Peak SB"
  timing: IntersectionTiming[];
}

/** Maximum number of timing plans per corridor */
export const MAX_PLANS_PER_CORRIDOR = 10;

/** Maximum number of corridors per user */
export const MAX_CORRIDORS_PER_USER = 10;

// ============================================================================
// Display Settings
// ============================================================================

/** Display toggle keys for Show Frame options */
export interface DisplayToggles {
  nbSequence: boolean;
  sbSequence: boolean;
  nbNextGreen: boolean;
  sbNextGreen: boolean;
  nbTravelPath: boolean;
  sbTravelPath: boolean;
  nbPhaseExtents: boolean;
  sbPhaseExtents: boolean;
  phaseLetter: boolean;
  phaseStartTime: boolean;
  intLabel: boolean;
}

/** Default display toggle values — all enabled */
export const DEFAULT_DISPLAY_TOGGLES: DisplayToggles = {
  nbSequence: true,
  sbSequence: true,
  nbNextGreen: true,
  sbNextGreen: true,
  nbTravelPath: false,
  sbTravelPath: false,
  nbPhaseExtents: true,
  sbPhaseExtents: true,
  phaseLetter: true,
  phaseStartTime: false,
  intLabel: true,
};

/** Corridor-level display and calculation settings */
export interface CorridorSettings {
  cycleLength: number;    // total cycle time in seconds (sum of phases)
  showGrid: boolean;
  showLabels: boolean;
  display: DisplayToggles;
}

// Alias for backward compatibility
export type PlanSettings = CorridorSettings;

// ============================================================================
// Corridor (Main Data Structure)
// ============================================================================

/** Complete corridor with multiple timing plans */
export interface Corridor {
  id: string;               // UUID
  name: string;             // Corridor name (e.g., "Main Street")
  description?: string;
  createdAt: string;        // ISO 8601
  updatedAt: string;        // ISO 8601
  intersections: IntersectionBase[];  // Shared across all plans
  plans: TimingPlan[];      // Up to 10 timing variations
  activePlanIndex: number;  // Which plan is currently displayed (0-based)
  settings: CorridorSettings;
}

/**
 * Legacy Plan type - alias for backward compatibility
 * @deprecated Use Corridor instead
 */
export interface Plan {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  intersections: Intersection[];  // Full intersections (merged view)
  settings: PlanSettings;
}

/** File import result */
export interface ImportResult {
  success: boolean;
  corridor?: Corridor;
  plan?: Plan;  // Legacy support
  errors?: string[];
  warnings?: string[];
}

// ============================================================================
// Merge/Split Helpers
// ============================================================================

/** Merge base intersection data with timing from active plan */
export function mergeIntersectionWithTiming(
  base: IntersectionBase,
  timing: IntersectionTiming | undefined
): Intersection {
  const defaultPhases: Phase[] = [
    { id: 'A', duration: 36, direction: 'NBSB' },
    { id: 'B', duration: 36, direction: 'NB' },
    { id: 'C', duration: 36, direction: 'SB' },
  ];

  return {
    ...base,
    offset: timing?.offset ?? 0,
    phases: timing?.phases ?? defaultPhases,
  };
}

/** Get merged intersections for the active plan */
export function getActiveIntersections(corridor: Corridor): Intersection[] {
  const activePlan = corridor.plans[corridor.activePlanIndex];
  if (!activePlan) {
    // No plans yet, return base with default timing
    return corridor.intersections.map(base => mergeIntersectionWithTiming(base, undefined));
  }

  return corridor.intersections.map(base => {
    const timing = activePlan.timing.find(t => t.intersectionId === base.id);
    return mergeIntersectionWithTiming(base, timing);
  });
}

/** Extract base intersection data from full intersection */
export function extractBase(intersection: Intersection): IntersectionBase {
  return {
    id: intersection.id,
    sequenceNo: intersection.sequenceNo,
    name: intersection.name,
    crossStreet: intersection.crossStreet,
    distanceToNext: intersection.distanceToNext,
    speedNorth: intersection.speedNorth,
    speedSouth: intersection.speedSouth,
  };
}

/** Extract timing data from full intersection */
export function extractTiming(intersection: Intersection): IntersectionTiming {
  return {
    intersectionId: intersection.id,
    offset: intersection.offset,
    phases: intersection.phases,
  };
}

/** Convert legacy Plan to new Corridor format */
export function planToCorridor(plan: Plan): Corridor {
  const intersectionBases = plan.intersections.map(extractBase);
  const timing = plan.intersections.map(extractTiming);

  return {
    id: plan.id,
    name: plan.name,
    description: plan.description,
    createdAt: plan.createdAt,
    updatedAt: plan.updatedAt,
    intersections: intersectionBases,
    plans: [{
      id: crypto.randomUUID(),
      name: 'Plan 1',
      timing,
    }],
    activePlanIndex: 0,
    settings: plan.settings,
  };
}

/** Convert Corridor to legacy Plan format (for export/compat) */
export function corridorToPlan(corridor: Corridor): Plan {
  return {
    id: corridor.id,
    name: corridor.name,
    description: corridor.description,
    createdAt: corridor.createdAt,
    updatedAt: corridor.updatedAt,
    intersections: getActiveIntersections(corridor),
    settings: corridor.settings,
  };
}

// ============================================================================
// Computed value helpers
// ============================================================================

/** Intersection with computed cumulative distance (Y position) */
export interface IntersectionPosition {
  intersection: Intersection;
  cumulativeDistance: number;  // Y position in metres from corridor start
}

/** Compute cumulative distances for all intersections */
export function computePositions(intersections: Intersection[]): IntersectionPosition[] {
  let cumulative = 0;
  return intersections.map((intersection) => {
    const pos = { intersection, cumulativeDistance: cumulative };
    cumulative += intersection.distanceToNext;
    return pos;
  });
}

/** Compute total corridor length (cumulative distance to the last intersection) */
export function computeCorridorLength(intersections: Intersection[] | IntersectionBase[]): number {
  if (intersections.length <= 1) return 0;
  // Exclude the last intersection's distanceToNext — it leads nowhere
  return intersections.slice(0, -1).reduce((sum, int) => sum + int.distanceToNext, 0);
}

/** Compute cycle length from an intersection's phases */
export function computeCycleLength(intersection: Intersection): number {
  return intersection.phases.reduce((sum, p) => sum + p.duration, 0);
}

// ============================================================================
// Bandwidth computation
// ============================================================================

interface GreenWindow { start: number; end: number }

/** Get all green windows for a direction at an intersection */
function getDirectionGreenWindows(
  intersection: Intersection,
  direction: 'north' | 'south',
  amberTime: number
): GreenWindow[] {
  const windows: GreenWindow[] = [];
  const phases = intersection.phases;
  let cumDur = 0;
  let i = 0;

  while (i < phases.length) {
    if (!phaseMatchesDirection(phases[i], direction)) {
      cumDur += phases[i].duration;
      i++;
      continue;
    }

    // Start of a consecutive matching run
    const runStart = cumDur;
    while (i < phases.length && phaseMatchesDirection(phases[i], direction)) {
      cumDur += phases[i].duration;
      i++;
    }

    // Deduct amber only at the end of the run
    const greenDur = (cumDur - runStart) - amberTime;
    if (greenDur > 0) {
      windows.push({
        start: intersection.offset + runStart,
        end: intersection.offset + runStart + greenDur,
      });
    }
  }
  return windows;
}

/** Check if a time falls within any green window (considering cycle wrapping) */
function isTimeInGreenWindows(time: number, windows: GreenWindow[], cycleLen: number): boolean {
  for (const w of windows) {
    if (w.end <= w.start) continue;
    const n = Math.floor((time - w.start) / cycleLen);
    for (let c = Math.max(0, n); c <= n + 1; c++) {
      const absStart = w.start + c * cycleLen;
      const absEnd = w.end + c * cycleLen;
      if (time >= absStart && time <= absEnd) return true;
    }
  }
  return false;
}

/**
 * Compute bandwidth for a direction (NB or SB).
 * Returns the width of the largest contiguous green window in seconds.
 */
export function computeBandwidth(
  intersections: Intersection[],
  direction: 'north' | 'south'
): number {
  if (intersections.length < 2) return 0;

  const cycleLength = computeCycleLength(intersections[0]);
  if (cycleLength <= 0) return 0;

  const amberTime = DEFAULTS.intersection.amber.defaultValue;
  const ordered = direction === 'south' ? [...intersections].reverse() : intersections;

  // Green windows at each intersection
  const allWindows = ordered.map(int => getDirectionGreenWindows(int, direction, amberTime));

  // Cumulative travel times from origin
  const positions = computePositions(intersections);
  const orderedPositions = direction === 'south' ? [...positions].reverse() : positions;
  const cumTravel: number[] = [0];
  for (let i = 0; i < ordered.length - 1; i++) {
    const speed = direction === 'north' ? ordered[i].speedNorth : ordered[i].speedSouth;
    const dist = Math.abs(orderedPositions[i + 1].cumulativeDistance - orderedPositions[i].cumulativeDistance);
    cumTravel.push(cumTravel[i] + dist / (speed / 3.6));
  }

  // Scan to find contiguous green bands
  const scanWidth = cycleLength * 2;
  const step = 0.5;
  let maxBandwidth = 0;
  let currentStart: number | null = null;

  for (let t = 0; t < scanWidth; t += step) {
    // Must depart during green at origin
    if (allWindows[0].length > 0 && !isTimeInGreenWindows(t, allWindows[0], cycleLength)) {
      if (currentStart !== null) {
        maxBandwidth = Math.max(maxBandwidth, t - currentStart);
        currentStart = null;
      }
      continue;
    }

    // Check arrival at downstream intersections
    let allGreen = true;
    for (let i = 1; i < ordered.length; i++) {
      if (allWindows[i].length === 0) continue;
      const arrival = t + cumTravel[i];
      if (!isTimeInGreenWindows(arrival, allWindows[i], cycleLength)) {
        allGreen = false;
        break;
      }
    }

    if (allGreen) {
      if (currentStart === null) currentStart = t;
    } else {
      if (currentStart !== null) {
        maxBandwidth = Math.max(maxBandwidth, t - currentStart);
        currentStart = null;
      }
    }
  }
  if (currentStart !== null) {
    maxBandwidth = Math.max(maxBandwidth, scanWidth - currentStart);
  }

  return Math.round(maxBandwidth * 10) / 10; // Round to 1 decimal
}

/** Create default settings based on intersection data */
export function computeDefaultSettings(intersections: Intersection[], existing?: CorridorSettings): CorridorSettings {
  const cycleLength = intersections.length > 0
    ? computeCycleLength(intersections[0])
    : DEFAULTS.plan.cycleLength.defaultValue;

  return {
    cycleLength,
    showGrid: true,
    showLabels: true,
    display: existing?.display ?? { ...DEFAULT_DISPLAY_TOGGLES },
  };
}
